import * as XLSX from 'xlsx';

export const EXAM_IMPORT_HEADERS = [
    '分配ID(勿改)',
    '姓名(勿改)',
    '身份证号(勿改)',
    '报考专业(勿改)',
    '考试地点',
    '考场号',
    '座位号',
    '考试科目',
    '考试日期(YYYY-MM-DD)',
    '开始时间(HH:mm:ss)',
    '结束时间(HH:mm:ss)',
];

const INSTRUCTION_ROWS = [
    ['考试安排导入模板 - 填写说明'],
    [''],
    ['1. 请勿修改「分配ID」「姓名」「身份证号」「报考专业」四列，系统据此匹配考生。'],
    ['2. 同一考生有多门考试时：复制该考生行，保持前四列一致，每行填写一门科目。'],
    ['3. 考试地点、考场号、座位号：同一考生只需在第一行填写，后续科目行可留空（会自动沿用）。'],
    ['4. 考试科目、考试日期、开始时间、结束时间为必填；日期格式 2026-06-01，时间格式 09:00:00。'],
    ['5. 仅「待分配」状态考生可导入；导入确认后将写入系统并变为「已分配」。'],
    [''],
    ['请在「考试安排」工作表中填写数据。'],
];

function cellText(value) {
    if (value === null || value === undefined) {
        return '';
    }
    return String(value).trim();
}

function normalizeTime(value) {
    if (value instanceof Date && !isNaN(value.getTime())) {
        return [
            String(value.getHours()).padStart(2, '0'),
            String(value.getMinutes()).padStart(2, '0'),
            String(value.getSeconds()).padStart(2, '0'),
        ].join(':');
    }
    if (typeof value === 'number' && !isNaN(value)) {
        if (value >= 0 && value < 1) {
            const totalSeconds = Math.round(value * 24 * 60 * 60);
            const h = Math.floor(totalSeconds / 3600) % 24;
            const m = Math.floor((totalSeconds % 3600) / 60);
            const s = totalSeconds % 60;
            return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
        }
    }
    const text = cellText(value);
    if (!text) {
        return '';
    }
    if (/^\d{1,2}:\d{2}$/.test(text)) {
        return text + ':00';
    }
    if (/^\d{1,2}:\d{2}:\d{2}$/.test(text)) {
        const parts = text.split(':');
        return [
            parts[0].padStart(2, '0'),
            parts[1].padStart(2, '0'),
            parts[2].padStart(2, '0'),
        ].join(':');
    }
    const dateLike = new Date(text);
    if (!isNaN(dateLike.getTime()) && text.indexOf(':') !== -1) {
        return [
            String(dateLike.getHours()).padStart(2, '0'),
            String(dateLike.getMinutes()).padStart(2, '0'),
            String(dateLike.getSeconds()).padStart(2, '0'),
        ].join(':');
    }
    return text;
}

function normalizeDate(value) {
    if (value instanceof Date && !isNaN(value.getTime())) {
        return [
            value.getFullYear(),
            String(value.getMonth() + 1).padStart(2, '0'),
            String(value.getDate()).padStart(2, '0'),
        ].join('-');
    }
    const text = cellText(value);
    if (!text) {
        return '';
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
        return text;
    }
    if (/^\d{4}\/\d{1,2}\/\d{1,2}$/.test(text)) {
        const parts = text.split('/');
        return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
    }
    if (typeof value === 'number' && !isNaN(value) && value > 1) {
        const parsed = XLSX.SSF.parse_date_code(value);
        if (parsed) {
            return `${parsed.y}-${String(parsed.m).padStart(2, '0')}-${String(parsed.d).padStart(2, '0')}`;
        }
    }
    const parsedDate = new Date(text);
    if (!isNaN(parsedDate.getTime()) && parsedDate.getFullYear() > 1970) {
        return [
            parsedDate.getFullYear(),
            String(parsedDate.getMonth() + 1).padStart(2, '0'),
            String(parsedDate.getDate()).padStart(2, '0'),
        ].join('-');
    }
    return text.substring(0, 10);
}

function formatExportFileTime() {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}

function formatExportDate(value) {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    if (value instanceof Date && !isNaN(value.getTime())) {
        return normalizeDate(value);
    }
    const text = String(value).trim();
    if (!text) {
        return '';
    }
    if (text.indexOf('T') !== -1) {
        return text.substring(0, 10);
    }
    if (text.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(text)) {
        return text.substring(0, 10);
    }
    return text;
}

function formatExportTime(value) {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    if (value instanceof Date && !isNaN(value.getTime())) {
        return normalizeTime(value);
    }
    const text = String(value).trim();
    if (!text) {
        return '';
    }
    if (/^\d{1,2}:\d{2}:\d{2}/.test(text)) {
        return text.substring(0, 8);
    }
    if (/^\d{1,2}:\d{2}$/.test(text)) {
        return text + ':00';
    }
    return text;
}

const EXPORT_INSTRUCTION_ROWS = [
    ['已分配考生考试安排导出'],
    [''],
    ['本文件列结构与「导入模板」一致，可直接用于备份或二次编辑。'],
    ['同一考生多门科目为多行数据；前四列请勿修改。'],
];

function buildExamWorkbook(dataRows, instructionRows) {
    const instructionSheet = XLSX.utils.aoa_to_sheet(instructionRows);
    const dataSheet = XLSX.utils.aoa_to_sheet([
        EXAM_IMPORT_HEADERS,
        ...dataRows,
    ]);

    instructionSheet['!cols'] = [{ wch: 88 }];
    dataSheet['!cols'] = [
        { wch: 12 }, { wch: 10 }, { wch: 20 }, { wch: 24 },
        { wch: 16 }, { wch: 10 }, { wch: 10 },
        { wch: 16 }, { wch: 18 }, { wch: 16 }, { wch: 16 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, instructionSheet, '填写说明');
    XLSX.utils.book_append_sheet(wb, dataSheet, '考试安排');
    return wb;
}

function buildAssignedExportRows(assignedStudents) {
    const rows = [];
    (assignedStudents || []).forEach((item) => {
        const schedules = item.examScheduleList || [];
        const base = [
            item.id,
            item.applicantName || '',
            item.idCardNumber || '',
            item.positionName || '',
        ];
        if (!schedules.length) {
            rows.push([
                ...base,
                item.examLocation || '',
                item.roomNumber || '',
                item.seatNumber || '',
                '', '', '', '',
            ]);
            return;
        }
        schedules.forEach((subject, index) => {
            rows.push([
                ...base,
                index === 0 ? (item.examLocation || '') : '',
                index === 0 ? (item.roomNumber || '') : '',
                index === 0 ? (item.seatNumber || '') : '',
                subject.examSubject || '',
                formatExportDate(subject.examDate),
                formatExportTime(subject.examTime),
                formatExportTime(subject.examTimeEnd),
            ]);
        });
    });
    return rows;
}

export function downloadExamImportTemplate(pendingStudents) {
    const list = Array.isArray(pendingStudents) ? pendingStudents : [];
    const dataRows = list.map((item) => ([
        item.id,
        item.applicantName || '',
        item.idCardNumber || '',
        item.positionName || '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
    ]));

    const wb = buildExamWorkbook(dataRows, INSTRUCTION_ROWS);
    XLSX.writeFile(wb, `考试安排导入模板-${formatExportFileTime()}.xlsx`);
}

export function downloadExamAssignedExport(assignedStudents) {
    const dataRows = buildAssignedExportRows(assignedStudents);
    const wb = buildExamWorkbook(dataRows, EXPORT_INSTRUCTION_ROWS);
    XLSX.writeFile(wb, `已分配考试安排-${formatExportFileTime()}.xlsx`);
}

function findDataSheet(workbook) {
    if (workbook.SheetNames.indexOf('考试安排') !== -1) {
        return workbook.Sheets['考试安排'];
    }
    return workbook.Sheets[workbook.SheetNames[0]];
}

function rowToObject(row) {
    const obj = {};
    EXAM_IMPORT_HEADERS.forEach((header, index) => {
        obj[header] = row[index];
    });
    return obj;
}

function isHeaderRow(row) {
    if (!row || !row.length) {
        return false;
    }
    const first = cellText(row[0]);
    return first.indexOf('分配ID') !== -1 || first === '分配ID(勿改)';
}

function isEmptyDataRow(row) {
    if (!row || !row.length) {
        return true;
    }
    return EXAM_IMPORT_HEADERS.every((_header, index) => cellText(row[index]) === '');
}

export function parseExamImportWorkbook(workbook, pendingStudents) {
    const pendingMap = {};
    (pendingStudents || []).forEach((item) => {
        pendingMap[String(item.id)] = item;
    });

    const sheet = findDataSheet(workbook);
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
    const parsedRows = [];
    const errors = [];

    rows.forEach((row, rowIndex) => {
        if (isHeaderRow(row) || isEmptyDataRow(row)) {
            return;
        }
        const item = rowToObject(row);
        const assignmentIdText = cellText(item['分配ID(勿改)']);
        if (!assignmentIdText) {
            errors.push(`第 ${rowIndex + 1} 行：分配ID不能为空`);
            return;
        }
        parsedRows.push({
            rowIndex: rowIndex + 1,
            assignmentId: assignmentIdText,
            applicantName: cellText(item['姓名(勿改)']),
            idCardNumber: cellText(item['身份证号(勿改)']),
            positionName: cellText(item['报考专业(勿改)']),
            examLocation: cellText(item['考试地点']),
            roomNumber: cellText(item['考场号']),
            seatNumber: cellText(item['座位号']),
            examSubject: cellText(item['考试科目']),
            examDate: normalizeDate(item['考试日期(YYYY-MM-DD)']),
            examTime: normalizeTime(item['开始时间(HH:mm:ss)']),
            examTimeEnd: normalizeTime(item['结束时间(HH:mm:ss)']),
            pending: pendingMap[assignmentIdText] || null,
        });
    });

    if (!parsedRows.length && !errors.length) {
        errors.push('未读取到有效数据，请使用系统下载的模板填写');
    }

    const groupedMap = {};
    parsedRows.forEach((row) => {
        if (!groupedMap[row.assignmentId]) {
            groupedMap[row.assignmentId] = {
                examAssignmentId: Number(row.assignmentId),
                applicantName: row.applicantName,
                idCardNumber: row.idCardNumber,
                positionName: row.positionName,
                examLocation: '',
                roomNumber: '',
                seatNumber: '',
                examScheduleList: [],
                rowIndexes: [],
                pending: row.pending,
            };
        }
        const group = groupedMap[row.assignmentId];
        group.rowIndexes.push(row.rowIndex);

        if (row.examLocation) {
            group.examLocation = row.examLocation;
        }
        if (row.roomNumber) {
            group.roomNumber = row.roomNumber;
        }
        if (row.seatNumber) {
            group.seatNumber = row.seatNumber;
        }

        if (row.examSubject || row.examDate || row.examTime || row.examTimeEnd) {
            group.examScheduleList.push({
                examSubject: row.examSubject,
                examDate: row.examDate,
                examTime: row.examTime,
                examTimeEnd: row.examTimeEnd,
                rowIndex: row.rowIndex,
            });
        }
    });

    const previewList = Object.keys(groupedMap).map((key) => groupedMap[key]);

    previewList.forEach((group) => {
        const label = group.applicantName || `分配ID=${group.examAssignmentId}`;
        if (!group.pending) {
            errors.push(`${label}：分配ID不在当前待分配列表中`);
        } else if (group.pending.status !== 0) {
            errors.push(`${label}：该考生不是待分配状态`);
        }
        if (group.pending) {
            if (group.applicantName && group.pending.applicantName
                && group.applicantName !== group.pending.applicantName) {
                errors.push(`${label}：姓名与系统不一致，请勿修改模板`);
            }
            if (group.idCardNumber && group.pending.idCardNumber
                && group.idCardNumber.toUpperCase() !== String(group.pending.idCardNumber).toUpperCase()) {
                errors.push(`${label}：身份证号与系统不一致，请勿修改模板`);
            }
        }
        if (!group.examLocation) {
            errors.push(`${label}：考试地点不能为空`);
        }
        if (!group.roomNumber) {
            errors.push(`${label}：考场号不能为空`);
        }
        if (!group.seatNumber) {
            errors.push(`${label}：座位号不能为空`);
        }
        if (!group.examScheduleList.length) {
            errors.push(`${label}：至少填写一门考试科目`);
        }
        group.examScheduleList.forEach((subject) => {
            if (!subject.examSubject) {
                errors.push(`${label} 第 ${subject.rowIndex} 行：考试科目不能为空`);
            }
            if (!subject.examDate) {
                errors.push(`${label} 第 ${subject.rowIndex} 行：考试日期不能为空`);
            }
            if (!subject.examTime) {
                errors.push(`${label} 第 ${subject.rowIndex} 行：开始时间不能为空`);
            }
            if (!subject.examTimeEnd) {
                errors.push(`${label} 第 ${subject.rowIndex} 行：结束时间不能为空`);
            }
        });
    });

    return {
        previewList,
        errors: Array.from(new Set(errors)),
        payload: previewList.map((group) => ({
            examAssignmentId: group.examAssignmentId,
            applicantName: group.applicantName,
            idCardNumber: group.idCardNumber,
            examLocation: group.examLocation,
            roomNumber: group.roomNumber,
            seatNumber: group.seatNumber,
            examScheduleList: group.examScheduleList.map((subject) => ({
                examSubject: subject.examSubject,
                examDate: subject.examDate,
                examTime: subject.examTime,
                examTimeEnd: subject.examTimeEnd,
            })),
        })),
    };
}

export function readExamImportFile(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const data = new Uint8Array(event.target.result);
                const workbook = XLSX.read(data, { type: 'array', cellDates: true });
                resolve(workbook);
            } catch (error) {
                reject(error);
            }
        };
        reader.onerror = () => reject(new Error('文件读取失败'));
        reader.readAsArrayBuffer(file);
    });
}

export function formatSubjectSummary(list) {
    if (!Array.isArray(list) || !list.length) {
        return '—';
    }
    return list.map((item) => {
        return `${item.examSubject} ${item.examDate} ${item.examTime}-${item.examTimeEnd}`;
    }).join('；');
}
